import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import type { Command } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useTransformNavigationCommands } from '../../../utils/transform-navigation-commands/index.tsx';
import type { PlanCommandProps } from './types.tsx';

export const useGetPlanCommand = () => {
	const { transformNavigationCommand } = useTransformNavigationCommands();

	const getPlanCommand = useCallback(
		({ plan, section, analytics, ...rest }: PlanCommandProps): Command => {
			const { id, url, name } = plan;

			return transformNavigationCommand(
				{
					id: `plan-${id}`,
					section,
					name,
					components: {
						LeftIcon: () => <PlanLeftIcon />,
					},
					analytics: {
						action: 'planViewed',
						planId: id,
						metadata: {
							contentId: id,
							containerId: 'UNAVAILABLE',
							contentType: 'plan',
						},
						...analytics,
					},
					...rest,
				},
				{ path: url },
			);
		},
		[transformNavigationCommand],
	);

	return { getPlanCommand };
};

export const PlanLeftIcon = () => (
	<PlanLeftIconContainer>
		<svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
			<path d="M6.44455 5.66667C6.015 5.66667 5.66677 6.01489 5.66677 6.44444C5.66677 6.874 6.015 7.22222 6.44455 7.22222H9.55566C9.98522 7.22222 10.3334 6.874 10.3334 6.44444C10.3334 6.01489 9.98522 5.66667 9.55566 5.66667H6.44455Z" />
			<path d="M6.44455 8.77778C6.44455 8.34822 6.79278 8 7.22233 8H10.3334C10.763 8 11.1112 8.34822 11.1112 8.77778C11.1112 9.20733 10.763 9.55556 10.3334 9.55556H7.22233C6.79278 9.55556 6.44455 9.20733 6.44455 8.77778Z" />
			<path d="M6.44455 10.3333C6.015 10.3333 5.66677 10.6816 5.66677 11.1111C5.66677 11.5407 6.015 11.8889 6.44455 11.8889H8.00011C8.42966 11.8889 8.77789 11.5407 8.77789 11.1111C8.77789 10.6816 8.42966 10.3333 8.00011 10.3333H6.44455Z" />
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M4.889 2.55556C4.889 1.69645 5.58544 1 6.44455 1H9.55566C10.4148 1 11.1112 1.69645 11.1112 2.55556H11.889C12.7481 2.55556 13.4446 3.252 13.4446 4.11111V13.4444C13.4446 14.3036 12.7481 15 11.889 15H4.11122C3.25211 15 2.55566 14.3036 2.55566 13.4444V4.11111C2.55566 3.252 3.25211 2.55556 4.11122 2.55556H4.889ZM9.55566 4.88889C10.1314 4.88889 10.6342 4.57607 10.9031 4.11111H11.889V13.4444H4.11122L4.11122 4.11111H5.0971C5.36607 4.57607 5.86878 4.88889 6.44455 4.88889H9.55566ZM9.55566 2.55556H6.44455V3.33333H9.55566V2.55556Z"
			/>
		</svg>
	</PlanLeftIconContainer>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PlanLeftIconContainer = styled.span({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > svg': {
		overflow: 'hidden',
		pointerEvents: 'none',
		maxWidth: '100%',
		maxHeight: '100%',
		fill: 'currentColor',
		verticalAlign: 'bottom',
	},
});
